import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TranslationKey, useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Loader } from '../../../../../../components/Loader';
import { IQuestionModel } from '../../../../question.model';
import { ProjectPickerOption } from './ProjectPickerOption';
import { ProjectPickerSelectAllOption } from './ProjectPickerSelectAllOption';

interface IProject {
  id: string;
  name: string;
  count: number;
  questions: IQuestionModel[];
}

interface IProps {
  options: IProject[];
  selected: string[];
  opened: string[];
  isLoaded: boolean;
  emptyListLabel?: TranslationKey;
  onChange: (projectIds: string[]) => void;
  onOpen: (projectIds: string[]) => void;
  onClose: () => void;
}

export const ProjectPicker = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  const areAllSelected = props.options.every((o) => props.selected.some((i) => i === o.id));

  const handleChange = (id: string, value: boolean): void => {
    const newValues = new Set(props.selected);
    if (value) {
      newValues.add(id);
    } else {
      newValues.delete(id);
    }
    props.onChange(Array.from(newValues));
  };

  const handleOpen = (id: string, value: boolean): void => {
    const newValues = new Set(props.opened);
    if (value) {
      newValues.add(id);
    } else {
      newValues.delete(id);
    }
    props.onOpen(Array.from(newValues));
  };

  const handleSelectAll = (value: boolean): void => {
    props.onChange(value ? props.options.map((o) => o.id) : []);
  };

  return (
    <div className='project-picker'>
      <div className='project-picker-title'>
        <div>
          <FontAwesomeIcon icon={faLink} /> {t('annotated_in_other_projects')}
        </div>
        {props.onClose && (
          <IconButton onClick={props.onClose}>
            <FontAwesomeIcon icon={'times'} />
          </IconButton>
        )}
      </div>
      {props.isLoaded && (
        <div className='description'>
          {props.options.length ? t('other_projects_description') : <b>{t(props.emptyListLabel ?? 'other_projects_description_zero')}</b>}
        </div>
      )}
      <Loader isLoading={!props.isLoaded}>
        <div className='projects'>
          {props.options.length > 0 && (
            <ProjectPickerSelectAllOption
              onChange={handleSelectAll}
              checked={areAllSelected}
            />
          )}
          {props.options.map((o) => (
            <ProjectPickerOption
              key={o.id}
              {...o}
              name={o.name !== '' ? o.name : `(${t('untitled')})`}
              questions={o.questions}
              checked={props.selected.some((i) => i === o.id)}
              opened={props.opened.some((i) => i === o.id)}
              onChange={handleChange}
              onOpen={handleOpen}
            />
          ))}
        </div>
      </Loader>
    </div>
  );
});
